import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  GripVertical,
  X,
  Save,
  Loader2,
  Layers,
  CheckCircle,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Upload,
  Image as ImageIcon,
} from 'lucide-react';
import slidersService, {
  SliderItem,
  CreateSliderPayload,
  SliderColorVariant,
} from '../../services/sliders.service';
import API_ENDPOINTS from '../../config/api';

// ─── Color palette helper ─────────────────────────────────────────────────────

const COLOR_OPTIONS: { value: SliderColorVariant; label: string; bg: string; preview: string }[] = [
  { value: 'mint',   label: 'Mint',   bg: 'bg-emerald-500', preview: '#10b981' },
  { value: 'pink',   label: 'Pink',   bg: 'bg-pink-500',    preview: '#ec4899' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-400',  preview: '#facc15' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500',  preview: '#a855f7' },
];

// ─── Toast notification ───────────────────────────────────────────────────────

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

// ─── Slide Form Modal ─────────────────────────────────────────────────────────

interface SlideFormModalProps {
  slide: SliderItem | null; // null = create mode
  onClose: () => void;
  onSave: (data: CreateSliderPayload) => Promise<void>;
  saving: boolean;
}

const SlideFormModal: React.FC<SlideFormModalProps> = ({ slide, onClose, onSave, saving }) => {
  const [form, setForm] = useState<CreateSliderPayload>({
    title: slide?.title ?? '',
    subtitle: slide?.subtitle ?? '',
    button_label: slide?.button_label ?? '',
    button_url: slide?.button_url ?? '',
    color_variant: slide?.color_variant ?? 'mint',
    order: slide?.order ?? 0,
    is_active: slide?.is_active ?? true,
    image_url: slide?.image_url ?? null,
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(slide?.image_url ? `${API_ENDPOINTS.UPLOADS}/${slide.image_url}` : null);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let finalPayload = { ...form };
    
    if (imageFile) {
      try {
        const uploadResult = await slidersService.uploadImage(imageFile);
        finalPayload.image_url = uploadResult.url;
      } catch (err) {
        // We handle the save error in the parent component, but we should probably 
        // alert the user here or pass the error up. For now, let's just proceed 
        // with the payload and let the parent handle the overall save.
      }
    }
    
    onSave(finalPayload);
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setImageError("File size exceeds 5MB limit. Please choose a smaller image.");
        e.target.value = ''; // Reset input
        return;
      }
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            {slide ? 'Edit Slide' : 'Add New Slide'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Discover our trainings"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <textarea
              rows={2}
              value={form.subtitle ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              placeholder="Optional short description..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition resize-none"
            />
          </div>

          {/* Button label + URL */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.button_label}
                onChange={(e) => setForm((f) => ({ ...f, button_label: e.target.value }))}
                placeholder="e.g. Explore"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.button_url}
                onChange={(e) => setForm((f) => ({ ...f, button_url: e.target.value }))}
                placeholder="/trainings"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Image</label>
            <div className="mt-1 flex items-center gap-4">
              <div 
                className={`flex-shrink-0 h-20 w-32 rounded-lg border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden ${!previewUrl ? 'border-dashed' : ''}`}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition">
                  <Upload className="h-4 w-4 text-gray-500" />
                  Upload Image
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                </label>
                <p className="mt-1.5 text-xs text-gray-500">
                  Optional. Overrides the default hero background pattern. <br/>
                  <span className="font-medium">Max 5MB (JPG, PNG, WebP).</span>
                </p>
                {imageError && (
                  <p className="mt-1.5 text-xs text-red-500 font-medium">
                    {imageError}
                  </p>
                )}
                {previewUrl && (
                   <button 
                     type="button"
                     onClick={() => {
                       setImageFile(null);
                       setPreviewUrl(null);
                       setImageError(null);
                       setForm(f => ({ ...f, image_url: null }));
                     }}
                     className="mt-1 text-xs text-red-500 hover:text-red-700"
                   >
                     Remove image
                   </button>
                )}
              </div>
            </div>
          </div>

          {/* Color variant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color Variant</label>
            <div className="flex gap-3">
              {COLOR_OPTIONS.map((c) => (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => setForm((f) => ({ ...f, color_variant: c.value }))}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-2 rounded-lg border-2 transition-all ${
                    form.color_variant === c.value
                      ? 'border-brand-primary shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span
                    className="h-6 w-6 rounded-full"
                    style={{ backgroundColor: c.preview }}
                  />
                  <span className="text-xs font-medium text-gray-600">{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Order + Active */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                min={0}
                value={form.order ?? 0}
                onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
              />
            </div>
            <div className="flex flex-col justify-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    form.is_active ? 'bg-brand-primary' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${
                      form.is_active ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {slide ? 'Save Changes' : 'Add Slide'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Slide Card ───────────────────────────────────────────────────────────────

interface SlideCardProps {
  slide: SliderItem;
  index: number;
  total: number;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const SlideCard: React.FC<SlideCardProps> = ({
  slide,
  index,
  total,
  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown,
}) => {
  const color = COLOR_OPTIONS.find((c) => c.value === slide.color_variant);

  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md ${
        slide.is_active ? 'border-gray-200' : 'border-gray-200 opacity-70'
      }`}
    >
      <div className="flex items-center gap-4 p-4">
        {/* Order controls */}
        <div className="flex flex-col items-center gap-0.5 text-gray-300 shrink-0">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-0.5 hover:text-brand-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <GripVertical className="h-4 w-4" />
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-0.5 hover:text-brand-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        {/* Color indicator */}
        <div
          className="h-12 w-2 rounded-full shrink-0"
          style={{ backgroundColor: color?.preview ?? '#ccc' }}
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900 truncate">{slide.title}</p>
            {!slide.is_active && (
              <span className="shrink-0 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                Inactive
              </span>
            )}
          </div>
          {slide.subtitle && (
            <p className="text-sm text-gray-500 truncate mt-0.5">{slide.subtitle}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: color?.preview }}
              />
              {color?.label}
            </span>
            <a
              href={slide.button_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"
            >
              {slide.button_label}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onToggleActive}
            title={slide.is_active ? 'Deactivate' : 'Activate'}
            className={`p-2 rounded-lg transition-colors ${
              slide.is_active
                ? 'text-emerald-600 hover:bg-emerald-50'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {slide.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
          <button
            onClick={onEdit}
            title="Edit"
            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            title="Delete"
            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const SliderManagementPage: React.FC = () => {
  const [slides, setSlides] = useState<SliderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<SliderItem | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // ── Toast helpers ──
  const addToast = useCallback((type: Toast['type'], message: string) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  // ── Load ──
  const loadSlides = useCallback(async () => {
    try {
      setLoading(true);
      const data = await slidersService.getAdminSliders();
      setSlides(data.sort((a, b) => a.order - b.order));
    } catch {
      addToast('error', 'Failed to load slides. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  // ── Create / Update ──
  const handleSave = async (payload: CreateSliderPayload) => {
    try {
      setSaving(true);
      if (editingSlide) {
        await slidersService.updateSlider(editingSlide.id, payload);
        addToast('success', 'Slide updated successfully.');
      } else {
        await slidersService.createSlider(payload);
        addToast('success', 'Slide created successfully.');
      }
      setModalOpen(false);
      setEditingSlide(null);
      await loadSlides();
    } catch {
      addToast('error', 'Failed to save slide. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ──
  const handleToggleActive = async (slide: SliderItem) => {
    try {
      await slidersService.updateSlider(slide.id, { is_active: !slide.is_active });
      setSlides((prev) =>
        prev.map((s) => (s.id === slide.id ? { ...s, is_active: !s.is_active } : s)),
      );
    } catch {
      addToast('error', 'Failed to update slide status.');
    }
  };

  // ── Delete ──
  const handleDelete = async (id: number) => {
    try {
      await slidersService.deleteSlider(id);
      setSlides((prev) => prev.filter((s) => s.id !== id));
      setDeleteConfirmId(null);
      addToast('success', 'Slide deleted.');
    } catch {
      addToast('error', 'Failed to delete slide.');
    }
  };

  // ── Reorder (move up/down) ──
  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];

    const reordered = newSlides.map((s, i) => ({ ...s, order: i }));
    setSlides(reordered);

    try {
      await slidersService.reorderSliders({
        items: reordered.map((s) => ({ id: s.id, order: s.order })),
      });
    } catch {
      addToast('error', 'Failed to reorder slides.');
      await loadSlides(); // revert
    }
  };

  const openCreate = () => {
    setEditingSlide(null);
    setModalOpen(true);
  };

  const openEdit = (slide: SliderItem) => {
    setEditingSlide(slide);
    setModalOpen(true);
  };

  const activeCount = slides.filter((s) => s.is_active).length;

  return (
    <div className="space-y-6">
      {/* ── Toast container ── */}
      <div className="fixed top-4 right-4 z-[100] space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-auto transition-all duration-300 ${
              t.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {t.type === 'success' ? (
              <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
            )}
            {t.message}
          </div>
        ))}
      </div>

      {/* ── Delete confirmation modal ── */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="h-14 w-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="h-7 w-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delete Slide?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This action cannot be undone. The slide will be permanently removed from the homepage.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Slider Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage the homepage hero slides — order, content, and visibility.
          </p>
        </div>
        <button
          id="add-slide-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary/90 transition shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Add Slide
        </button>
      </div>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Slides', value: slides.length, color: 'text-gray-900' },
          { label: 'Active', value: activeCount, color: 'text-emerald-600' },
          { label: 'Inactive', value: slides.length - activeCount, color: 'text-gray-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
            <Layers className="h-8 w-8 text-brand-primary/30 shrink-0" />
            <div>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Slides list ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Loader2 className="h-10 w-10 animate-spin mb-3" />
          <p className="text-sm">Loading slides...</p>
        </div>
      ) : slides.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
          <Layers className="h-12 w-12 mb-3 text-gray-300" />
          <p className="font-medium text-gray-600">No slides yet</p>
          <p className="text-sm mt-1">Click "Add Slide" to create the first homepage slide.</p>
          <button
            onClick={openCreate}
            className="mt-5 flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition"
          >
            <Plus className="h-4 w-4" />
            Add First Slide
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {slides.map((slide, index) => (
            <SlideCard
              key={slide.id}
              slide={slide}
              index={index}
              total={slides.length}
              onEdit={() => openEdit(slide)}
              onDelete={() => setDeleteConfirmId(slide.id)}
              onToggleActive={() => handleToggleActive(slide)}
              onMoveUp={() => handleMove(index, 'up')}
              onMoveDown={() => handleMove(index, 'down')}
            />
          ))}
        </div>
      )}

      {/* ── Form Modal ── */}
      {modalOpen && (
        <SlideFormModal
          slide={editingSlide}
          onClose={() => {
            setModalOpen(false);
            setEditingSlide(null);
          }}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
};

export default SliderManagementPage;
